<template>
  <div class="main-box clinic-payment-history-box" v-loader.opacity="loading">
    <BaseTable
      class="no-border"
      v-if="tablePayments && tablePayments.items?.length"
      :tabledata="tablePayments">
      <template v-slot:column_id="{item}">
        <span :class="{ 'informative-text': item.isLine }">
          {{ item.id }}
        </span>
      </template>

      <template v-slot:column_subscription_date="{item}">
        <span :class="{ 'informative-text': item.isLine }">
          {{ formatDate(item.subscription_date) }}
        </span>
      </template>

      <template v-slot:column_plan_name="{item}">
        <span :class="{ 'informative-text': item.isLine }">
          {{ item.plan_name }}
        </span>
      </template>

      <template v-slot:column_status="{item}">
        <span v-if="item.status" class="label" :class="getStatusClass(item)">
          {{ $t(`plans.status.${item.status}`) }}
        </span>
        <span v-else :class="{ 'informative-text': item.isLine }">-</span>
      </template>

      <template v-slot:column_amount="{item}">
        <span class="currency" :class="{ 'currency-credit': item.isCredit, 'informative-amount': item.isLine }">
          {{ item.amount }}
        </span>
      </template>

      <template v-slot:column_payment_method="{item}">
        <span class="payment-method" :class="{ 'informative-text': item.isLine }">
          {{ formatPaymentMethod(item.payment_method) }}
        </span>
      </template>

      <template v-slot:column_period="{item}">
        <span :class="{ 'informative-text': item.isLine }">
          {{ item.formatted_period }}
        </span>
      </template>
    </BaseTable>

    <div class="no-results-box" v-else>
      <BaseNoResults :title="$t('request_notfound_title')" :text="$t('plans.noPaymentHistory')"/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.main-box {
  padding:                       0 10px 10px 10px;
}

.label {
  @include                       border_radius(12px);
  font-size:                     12px;
  font-weight:                   600;
  text-transform:                uppercase;
  letter-spacing:                0.5px;
  display:                       inline-block;
  padding:                       4px 10px;
}

.label-danger {
  color:                         var(--Complementary-Orange) !important;
  border:                        1px solid var(--Complementary-Orange);
  background:                    var(--Complementary-Orange-BG) !important;
}

.label-secondary {
  color:                         var(--Complementary-Grey);
  border:                        1px solid var(--Complementary-Grey);;
  background:                    var(--Complementary-Grey-BG);;
}

.label-info {
  color:                         var(--Complementary-Blue);
  border:                        1px solid var(--Complementary-Blue);
  background:                    var(--Complementary-Blue)-BG;
}

a {
  display:                       flex;
  align-items:                   center;
  gap:                           0.5rem;
}

.btn-outline-secondary {
  border-color:                  var(--Shades-50);
}

.currency-credit {
  color:                         var(--Complementary-Success);
  font-weight:                   600;
}


.informative-text {
  color:                         var(--Shades-50) !important;
  opacity:                       0.7 !important;
  font-size:                     0.9em;
}

.informative-amount {
  opacity:                       0.7 !important;
  font-size:                     0.9em;
}

@media (max-width: 991px) {
  .main-box {
    height:                      calc(100dvh - 258px);
    max-height:                  calc(100dvh - 258px);
    padding:                     0;
  }

  .has-alert .main-box {
    height:                      calc(100dvh - 328px);
  }
}

@media (min-width: 992px) {
  .main-box {
    overflow:                      hidden;
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'

// Portuguese/English words cover our own legacy statuses; the rest (open,
// draft, void, uncollectible) are Stripe's own vocabulary passed straight
// through by the backend.
const STATUS_CLASS_MAP = {
  'label-success': ['pago', 'ativo', 'paid', 'active'],
  'label-warning': ['pendente', 'pending', 'processando', 'open'],
  'label-danger': ['cancelado', 'canceled', 'estornado', 'void', 'uncollectible'],
  'label-secondary': ['expirado', 'expired', 'draft'],
};

export default {
  name: 'ClinicPaymentHistoryTab',
  data: () => ({
    loading: false,
    alert: {
      message: null,
      class: null
    },
    rawItems: [],
    tableTotal: 0,
    currentPage: 1,
    scrollTopCallback: null,
    dateFormat: null,
  }),
  computed: {
    tableColumns() {
      return [
        { key: 'id', name: 'ID', type: 'custom', value: 'id', width: '13%' },
        { key: 'subscription_date', name: this.$i18n.t('plans.adhesionDate'), type: 'custom', value: 'subscription_date', width: '12%' },
        { key: 'status', name: this.$i18n.t('status'), type: 'custom', value: 'status', width: '10%', align: 'text-center' },
        { key: 'plan_name', name: this.$i18n.t('plans.plans'), type: 'custom', value: 'plan_name', width: '10%' },
        { key: 'payment_method', name: this.$i18n.t('plans.paymentMethod'), type: 'custom', value: 'payment_method', width: '20%' },
        { key: 'period', name: this.$i18n.t('plans.period'), type: 'custom', value: 'period', width: '24%' },
        { key: 'amount', name: this.$i18n.t('plans.value'), type: 'custom', value: 'amount', width: '10%', align: 'text-right' },
      ];
    },

    tablePayments() {
      if (!this.rawItems.length) {
        return null;
      }

      return {
        items: this.rawItems,
        total: this.tableTotal,
        columns: this.tableColumns,
      };
    },
  },
  async mounted() {
    const userStore = useUserStore();
    this.session = userStore.sessionInfo;
    this.dateFormat = this.$i18n.t('dateTimeFormat')
    this.fetchData();
  },
  provide() {
    return {
      scrollToTop: (callback) => {
        this.scrollTopCallback = callback;
      }
    };
  },
  methods: {
    async fetchData() {
      await this.$helpers.requestHandling(async () => {
        const params = {
          page: this.currentPage,
          clinicUUID: this.session.currentClinic.uuid
        };

        const response = await this.$stripeService.getPaymenthistory(params);

        this.rawItems = response.result.flatMap(item => this.normalizeInvoice(item));
        this.tableTotal = response.totalCount || response.result.length;

        if (response.result.length === 0) {
          this.$message.popup(this, 'plans.noPaymentHistory', 'operationWarning', 'warn')
        }
      }, this);
    },

    // "plan_change" and "credit" invoices carry a real credit/charge
    // breakdown in `lines`. We show the invoice's own row (real status/plan/
    // total) plus that breakdown underneath, informative only.
    normalizeInvoice(item) {
      if (item.lines?.length) {
        return [this.buildPrimaryRow(item), ...this.buildLineRows(item)];
      }

      return [this.buildPrimaryRow(item)];
    },

    buildPrimaryRow(item) {
      const isCredit = item.type === 'credit';
      // "credit" invoices have no plan_name - use the plan from their charge line.
      const chargeLine = isCredit && item.lines?.find(line => !line.isCredit);

      return {
        ...item,
        formatted_period: this.formatPeriod(item.period_start, item.period_end),
        plan_name: isCredit ? (chargeLine?.planName || this.$i18n.t('plans.creditGenerated')) : item.plan_name,
        // Nothing charged to a card - fully covered by the old plan's credit.
        payment_method: isCredit ? this.$i18n.t('plans.paymentCompensation') : item.payment_method,
        isCredit,
      };
    },

    formatPeriod(start, end) {
      return `${moment(start).format(this.dateFormat)} ${this.$i18n.t('until')} ${moment(end).format(this.dateFormat)}`;
    },

    buildLineRows(item) {
      const formatted_period = this.formatPeriod(item.period_start, item.period_end);

      return item.lines.map((line, index) => ({
        id: item.id,
        isLine: true,
        subscription_date: item.subscription_date,
        // Informative only - the row above carries the real status.
        status: null,
        // No plan on the credit line - it's a refund, not a purchase.
        plan_name: line.isCredit ? '-' : line.planName,
        payment_method: this.$i18n.t(line.isCredit ? 'plans.unusedCredit' : 'plans.proportionalCharge'),
        formatted_period,
        amount: line.amount,
        isCredit: line.isCredit,
      }));
    },

    goTop() {
      if (this.scrollTopCallback) {
        this.scrollTopCallback();
      }
      this.currentPage = 1;
      this.fetchData();
    },

    // Método para determinar a classe do status
    getStatusClass(item) {
      const status = item.status || '';
      const entry = Object.entries(STATUS_CLASS_MAP).find(
        ([, keywords]) => keywords.some(keyword => status.includes(keyword))
      );

      return entry ? entry[0] : 'label-info';
    },

    formatPaymentMethod (payment) {
      if (!payment) {
        return '-'
      }

      if (payment === 'N/A') {
        return this.$t('plans.paymentCompensation');
      }
      return payment;
    },

    formatDate(value) {
      let result = moment(value).format(this.dateFormat)
      return result
    },
  }
}
</script>
